import { syncPdpIdentity } from '@pogi/pdp-refinements';
import { StandardEvents } from '@pogi/standard-events';
import {
  calculateShopifyUnitSavings,
  formatShopifyMoney,
  formatShopifyMoneyWithTemplate,
  formatShopifyUnitMoney,
  formatShopifyWholeMoney,
  resolveShopifyPdpPrice,
} from '@pogi/money';
import { PogiBuyBoxModel, readPogiBuyBoxContract } from '@pogi/buy-box-v2-core';
import {
  PogiBuyBoxHorizonAdapter,
  readPogiBuyBoxNativeState,
  revealPogiBuyBoxNativeControls,
} from '@pogi/buy-box-v2-horizon';
import { bindPdpUsage, normalizeDailyUsage, pdpUsageDuration, formatPdpUsageNumber, pdpUsageEstimate, formatPdpUsageDuration, renderPdpUsage, renderPdpIntervalSelect, resolveAvailablePlanSet as resolveConfiguredPlanSet, resolveInitialSellingPlanId } from '@pogi/buy-box-v2-subscription-config';

/*
 * pogi-buy-box-compostable — §5.1a, ported verbatim from the BuyBox component in
 * pdp-compostable-components.jsx. Axes = FORMAT × COUNT (unscented only). The
 * embedded ladder model (formats→counts) is the RENDER source of truth; selecting
 * a (format, count) maps onto the real Shopify variant by option value to drive
 * Horizon's hidden native variant-picker + form (cart, selling plan stay native).
 * Subscribe pricing uses the selected variant's exact live selling-plan
 * allocation and fails closed when that allocation is unavailable. Review count
 * (Judge.me) stays dynamic via Liquid.
 *
 * Role eyebrow per count: explicit `tag` (clay-deep) wins, else a per-label role,
 * else the count subtitle — so no card is ever left blank (mirrors the JSX).
 */

/* i18n: strings come from the data blob's `i18n` object (Liquid renders them via
   `| t` from locales/*.json pogi.bb.*). EVERY read falls back to the exact English
   literal that used to be hardcoded, so a missing key can never change the EN site.
   [[var]] placeholders (not {{ }}) so Liquid's t-filter never eats them. */
const pogiTpl = (I, k, fb, vars) => {
  let s = (I && I[k]) || fb;
  if (vars) for (const key of Object.keys(vars)) s = s.split('[[' + key + ']]').join(String(vars[key]));
  return s;
};

/* Role per count card, keyed by the BAGS number (stable across locales — the
   labels are translated per locale, the bag counts never change). k = i18n key,
   t = the exact English fallback. */
const ROLE_BY_BAGS = {
  135: { k: 'onthego', t: 'On the go' },
  270: { k: 'popular', t: 'Most popular', tone: 'accent' },
  480: { k: 'value', t: 'Best value', tone: 'accent' },
  140: { k: 'everyday', t: 'Everyday' },
  300: { k: 'stockup', t: 'Stock up', tone: 'olive' },
  120: { k: 'everyday', t: 'Everyday' },
  250: { k: 'stockup', t: 'Stock up', tone: 'olive' },
};
// Per-format line glyph — depicts the physical FORMAT (roll stack / jumbo roll /
// tie-handle bag), not the packaging. Same set as pogi-buy-box-bags, keyed to the
// compostable format keys (rolls / bigroll / handle). All normalized to the same
// vertical extent so the row reads even. Lucide stroke style (currentColor).
const FORMAT_GLYPH = {
  // Circles are TANGENT (hexagonal packing: row offset = r·√3, neighbors = 2r) so the
  // rolls touch like a real stack — floating gaps read wrong (owner 2026-06-12).
  'rolls': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="4.9" cy="18.2" r="3.55"/><circle cx="12" cy="18.2" r="3.55"/><circle cx="19.1" cy="18.2" r="3.55"/><circle cx="8.45" cy="12.05" r="3.55"/><circle cx="15.55" cy="12.05" r="3.55"/><circle cx="12" cy="5.9" r="3.55"/><circle cx="4.9" cy="18.2" r="1"/><circle cx="12" cy="18.2" r="1"/><circle cx="19.1" cy="18.2" r="1"/><circle cx="8.45" cy="12.05" r="1"/><circle cx="15.55" cy="12.05" r="1"/><circle cx="12" cy="5.9" r="1"/></svg>',
  'bigroll': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="3.5" rx="5.8" ry="2"/><path d="M6.2 3.5v17a5.8 2 0 0 0 11.6 0v-17"/><ellipse cx="12" cy="3.5" rx="1.9" ry="0.7"/></svg>',
  'handle': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5.2 20L4.8 11.8Q4.7 11 5.6 11H7V3Q7 1.6 8.5 1.6Q10 1.6 10 3V8.2Q12 10 14 8.2V3Q14 1.6 15.5 1.6Q17 1.6 17 3V11H18.4Q19.3 11 19.2 11.8L18.8 20Q18.7 22.4 16.4 22.5H7.6Q5.3 22.4 5.2 20Z"/></svg>',
};

/* POGI L10N FIX (2026-07-23): selling-plan names are LOCALIZED per storefront
   locale (9 locales: en/ja/fr/de/es/it + nl/pl/pt queued). The old parsers were
   EN(+JA)-only, so every EU plan name parsed null and interval matching fell
   back to wrong plans off-EN. Unit-word alternations + a no-digit=every-1 branch
   (fr «tous les mois», de „jeden Monat", es «cada mes», pl „co miesiąc», pt
   «mensal»). Match order month→week→day MUST hold: PL week words (tydzień/
   tygodni) contain the day stems (dzie/dni). Trip-tested against all live plan
   names ×6 locales + nl/pl/pt synthetics. */
const POGI_FREQ_WORDS = {
  month: 'month|か月|ヶ月|mois|monat|maand|miesi|mensil|mensual|mensal|mese|mesi|meses|mês|mes\\b',
  week: 'week|weke|週間|semaine|woche|wochen|settiman|semanas|semana|tygod|tydzi|semanal|wekelijks',
  day: 'day|日ごと|jour|tage|tag\\b|giorn|día|dias|dag\\b|dagen|dzie|dni\\b|dia\\b',
};
const pogiParseFreq = (raw) => {
  const s = String(raw).toLowerCase().replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xFEE0));
  // Pass 1: explicit "N <unit>" wins; with several numeric clauses the LAST one is
  // the frequency ("1 month supply – delivery every 2 weeks" / "Vorrat für einen
  // Monat – Lieferung alle 2 Wochen" must both parse week:2, never month:1).
  // matchAll (not match, which only sees the FIRST per-unit hit) so SAME-unit
  // compounds also keep the last clause ("2 weeks starter – every 4 weeks" = week:4).
  let best = null;
  for (const u of ['month', 'week', 'day']) {
    for (const m of s.matchAll(new RegExp('(\\d+)\\s*(?:' + POGI_FREQ_WORDS[u] + ')', 'g'))) {
      if (!best || m.index > best.idx) best = { u: u, n: parseInt(m[1], 10), idx: m.index };
    }
  }
  if (best) return { u: best.u, n: best.n };
  // Pass 2: a bare unit word = every-1 (fr «tous les mois», de „jeden Monat", pl „co miesiąc").
  for (const u of ['month', 'week', 'day']) {
    if (new RegExp(POGI_FREQ_WORDS[u]).test(s)) return { u: u, n: 1 };
  }
  return null;
};

// A11Y (audit P3, 2026-07-23): the buy box's radio-group containers, for the
// roving-tabindex pattern. .pogi-bb__purchase and [data-pogi-freq-opts] hold
// role="radio" buttons without a radiogroup role in the Liquid, so they are
// matched explicitly. The sizing modal's radios are deliberately NOT covered —
// it owns its own DOM lifecycle. Selectors absent from a given clone are
// harmless (querySelectorAll/closest simply never match).
const POGI_ROVE_GROUPS = '[data-pogi-formats], [data-pogi-sizes], [data-pogi-tiles], [data-pogi-counts], .pogi-bb__purchase, [data-pogi-freq-opts]';
// Eligible = focusable-for-roving: not disabled, not [hidden], and rendered.
// offsetParent is the visibility check: it is null when the radio OR any
// ancestor is display:none (this clone hides whole panels via [hidden]) — one
// check covers both, and none of these radios are position:fixed (the one
// case offsetParent cannot see). Chosen over getComputedStyle for cost
// (Codex countersign #19, 2026-07-23).
const POGI_ROVE_ELIGIBLE = (b) => !b.disabled && !b.hidden && b.offsetParent !== null;

class PogiBuyBoxCompostable extends HTMLElement {
  getDailyUsage() { return normalizeDailyUsage(this.usagePerDay, this.estPerDay || 2); }
  getUsageDuration(units) { return pdpUsageDuration(units, this.getDailyUsage(), 'months'); }
  setDailyUsage(value) {
    if (value != null && String(value).trim() && Number.isFinite(Number(value))) this.pogiUsageEdited = true;
    this.usagePerDay = normalizeDailyUsage(value, this.getDailyUsage());
    this.update();
    return this.usagePerDay;
  }
  usageNumber(value) { return formatPdpUsageNumber(value, this.v2Contract?.market?.displayLocale); }
  getUsageEstimate(units) { return pdpUsageEstimate(units, this.getDailyUsage(), 'months'); }
  usageDurationText(estimate) { return formatPdpUsageDuration(estimate, this.v2Contract?.market?.displayLocale); }
  connectedCallback() {
    if (this.closest('.shopify-section')?.hasAttribute('data-pogi-buy-box-fallback')) {
      this.setAttribute('inert', '');
      this.setAttribute('aria-busy', 'false');
      delete this.dataset.pogiV2Owner;
      return;
    }
    const reconnectState = readPogiBuyBoxNativeState(this);
    this.v2Events?.abort();
    this.v2QuantityObserver?.disconnect();
    this.v2Events = new AbortController();
    const dataEl = this.querySelector('[data-pogi-model]');
    if (!dataEl) return;
    try { this.data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    this.I = this.data.i18n || {};
    this.t = (k, fb, vars) => pogiTpl(this.I, k, fb, vars);
    bindPdpUsage(this);

    this.formats = this.data.formats || [];
    if (!this.formats.length) return;
    this.moneyFormat = this.data.moneyFormat || '${{amount}}';
    this.currency = String(this.data.currency || 'USD').toUpperCase(); // presentment ISO for market-aware formatting
    const shipThreshold = parseInt(this.dataset.shipThreshold, 10);
    this.shipThreshold = Number.isFinite(shipThreshold) ? shipThreshold : 0;

    this.section = this.closest('.shopify-section') || document;
    this.nativePicker = this.section.querySelector('variant-picker');
    this.nativeForm = document.getElementById(this.dataset.formId);

    this.v2Mode = ['observe', 'active'].includes(this.dataset.v2Mode) ? this.dataset.v2Mode : 'off';
    this.v2Contract = null;
    this.v2Model = null;
    this.v2Adapter = null;
    if (this.v2Mode !== 'off') {
      try {
        this.v2Contract = readPogiBuyBoxContract(this);
        this.v2Model = new PogiBuyBoxModel(this.v2Contract);
        if (this.v2Mode === 'active') {
          this.v2Adapter = new PogiBuyBoxHorizonAdapter(this, this.v2Model);
          this.v2Adapter.claim();
          this.dataset.pogiV2Owner = 'true';
        }
      } catch (error) {
        this.dataset.pogiV2Observe = 'invalid';
        console.error('[pogi-buy-box-v2] Compostable contract/ownership initialization failed:', error);
        if (this.v2Mode === 'active') {
          this.#failV2(error);
          return;
        }
      }
    }

    try {

    // Detect which native option position holds the format names vs the count
    // labels, so selections can drive the hidden native picker for add-to-cart.
    this.#detectOptionPositions();

    // Initial state — the Liquid-selected variant when resolvable (POGI FIX
    // 2026-07-23, audit L8-002: the box used to ignore data.selectedId and
    // highlight its own pick), else format[0] with its default count.
    const selectedId = reconnectState?.variantId || this.data.selectedId;
    const selV = (this.data.variants || []).find((v) => String(v.id) === String(selectedId)) || null;
    const selFC = this.#countForVariant(selV);
    this.format = (selFC && selFC.format) || this.formats[0];
    this.count = (selFC && selFC.count) || this.format.counts[1] || this.format.counts[0];
    this.freq = this.count.defaultFreq;
    this.subscribe = false; // one-time unless an exact initial plan or native reconnect state is valid
    const initialPlanId = reconnectState
      ? reconnectState.sellingPlanId
      : resolveInitialSellingPlanId(this.v2Model, this.#matchedVariant()?.id, globalThis.location?.search, true);
    this.pogiExplicitInterval = Boolean(initialPlanId);
    if (initialPlanId) {
      const retainedFrequency = this.#availableFrequencies().find((frequency) => {
        const plan = this.#planForFrequency(frequency);
        return String(plan?.id || '') === initialPlanId
          && Boolean(this.#allocationFor(this.#matchedVariant(), plan));
      });
      if (retainedFrequency != null) {
        this.freq = retainedFrequency;
        this.subscribe = true;
      }
    }
    const selectedV2Variant = this.v2Model?.resolveVariant(this.#v2Selections(this.count));
    if (reconnectState && this.v2Mode === 'active') {
      this.v2Adapter.updateCurrentCartQuantityFromForm();
    }
    this.qty = this.v2Mode === 'active' && selectedV2Variant
      ? this.v2Adapter.quantityState(
        selectedV2Variant.id,
        reconnectState?.quantity ?? selectedV2Variant.quantityRule.min,
      ).value
      : reconnectState?.quantity ?? 1;

    this.#renderFormats();
    this.#ensureSellingPlanInput();
    this.#bind();
    this.section.addEventListener(StandardEvents.productSelect, (event) => {
      event.promise?.then(() => {
        this.update();
      }).catch((error) => {
        if (error?.name === 'AbortError') return;
        if (this.v2Mode === 'active') this.#failV2(error);
      });
    }, { signal: this.v2Events.signal });
    if (this.v2Mode === 'active') {
      document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
        event.promise?.then(({ detail }) => {
          if (!this.isConnected || !this.v2Adapter?.updateCartItems(detail?.items)) return;
          this.update();
        }).catch((error) => {
          if (error?.name !== 'AbortError') {
            console.warn('[pogi-buy-box-v2] Compostable cart quantity refresh failed:', error);
          }
        });
      }, { signal: this.v2Events.signal });
      this.v2QuantityObserver = new MutationObserver((records) => {
        const changedInput = records.find((record) =>
          record.type === 'attributes'
          && record.attributeName === 'data-cart-quantity'
          && record.target instanceof HTMLInputElement)?.target;
        if (
          this.v2Mode !== 'active'
          || !(changedInput instanceof HTMLInputElement)
          || !this.v2Adapter?.updateCurrentCartQuantityFromForm(changedInput)
        ) return;
        this.update();
      });
      this.v2QuantityObserver.observe(this.v2Adapter.form, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['data-cart-quantity'],
      });
    }
    this.#renderCounts();
    this.#renderFreqs();
    this.#driveNative(); // L8-002: sync the native picker to the initial selection
    this.update();
    this.#injectGalleryBadge();
    this.#fixPreviewLinks('bags');
    this.querySelectorAll('[data-pogi-skeleton]').forEach((node) => node.remove());
    this.setAttribute('data-ready', '');
    this.removeAttribute('inert');
    this.setAttribute('aria-busy', 'false');
    this.dispatchEvent(new CustomEvent('pogi:buybox-ready', { bubbles: true }));
    } catch (error) {
      if (this.v2Mode === 'active') {
        console.error('[pogi-buy-box-v2] Compostable active initialization failed:', error);
        this.#failV2(error);
        return;
      }
      throw error;
    }
  }

  disconnectedCallback() {
    this.v2Events?.abort();
    this.v2QuantityObserver?.disconnect();
    this.v2Adapter?.release();
  }

  #failV2(error) {
    this.v2Events?.abort();
    this.v2QuantityObserver?.disconnect();
    this.v2Mode = 'failed';
    if (this.v2Adapter) this.v2Adapter.revealNativeControls(error);
    else revealPogiBuyBoxNativeControls(this, error);
    delete this.dataset.pogiV2Owner;
  }

  // In preview (?preview_theme_id / ?view present), carry the preview context onto the
  // material-toggle link so compostable⇄bags navigation stays on the bespoke templates.
  // No-op on the live storefront — no preview params → links stay clean.
  #fixPreviewLinks(view) {
    let params;
    try { params = new URLSearchParams(location.search); } catch (e) { return; }
    const pt = params.get('preview_theme_id');
    if (!pt && !params.get('view')) return;
    this.querySelectorAll('a.pogi-bb__mat[href]').forEach((a) => {
      try {
        const u = new URL(a.getAttribute('href'), location.origin);
        u.searchParams.set('view', view);
        if (pt) u.searchParams.set('preview_theme_id', pt);
        a.setAttribute('href', u.pathname + u.search);
      } catch (e) { /* leave as-is */ }
    });
  }

  /* ---------- helpers ---------- */
  #esc(s) { return String(s).replace(/"/g, '&quot;').replace(/</g, '&lt;'); }
  // Display: show "18 Rolls" big + "270 Bags" as the subtitle so the long label can't
  // overflow the card; the full "18 Rolls (270 Bags)" stays the ATC match value.
  // Jumbo/Tie labels ("140 Bags") have no parenthetical → pass through unchanged.
  #countHead(label) { return String(label).split(/[（(]/)[0].trim() || String(label); }
  #countSub(c) { const m = String(c.label).match(/[（(]([^）)]+)[）)]/); return (c.subtitle && c.subtitle.length) ? c.subtitle : (m ? m[1].trim() : ''); }

  money(cents) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';
    // Match the Liquid `money` output beside JS-rendered prices and totals.
    if (this.data?.moneyFormat) return formatShopifyMoneyWithTemplate(value, this.data.moneyFormat, this.currency || 'USD');
    return formatShopifyMoney(value, String(this.moneyFormat || '{{amount}}'), this.currency || 'USD');
  }
  moneyWhole(cents) {
    return formatShopifyWholeMoney(cents, this.moneyFormat, this.currency || 'USD');
  }
  unitMoney(cents, count) {
    return formatShopifyUnitMoney(cents, count, this.currency || 'USD');
  }
  showDerived() { return this.currency === 'USD'; }

  // Primary unit-price display always comes from the matched Shopify variant's
  // presentment price. The hardcoded ladder remains merchandising metadata only;
  // a missing native variant never creates a purchasable fallback price.
  // Width-fold so locale strings match option values regardless of （）/() width.
  #wnorm(s) { return String(s).replace(/（/g, '(').replace(/）/g, ')').replace(/　/g, ' ').trim(); }
  /* POGI FIX 2026-07-23 (audit L4-001): the model's display labels come from theme
     translations while variant options are registered translations — the two
     sources drifted in 32 EU label pairs (NBSP vs space etc.), so label-equality
     matching failed per locale. The locale-proof contract: every count has a
     globally UNIQUE bags number (135/270/480/140/300/120/250, and roll counts
     9/18/32 collide with none of them), and digits survive every locale — a
     variant belongs to a count iff the count's bags number appears among the
     numbers of one of its option values. Labels are display-only from here on. */
  #numsIn(s) { return (String(s == null ? '' : s).match(/\d[\d,]*/g) || []).map((n) => parseInt(n.replace(/,/g, ''), 10)); }
  #legacyVariantForCount(count) {
    if (!count) return null;
    return (this.data.variants || []).find((v) => (v.options || []).some((o) => this.#numsIn(o).includes(count.bags))) || null;
  }
  #v2Selections(count) {
    const legacyVariant = this.#legacyVariantForCount(count);
    if (!legacyVariant) return {};
    return Object.fromEntries((this.v2Contract?.axes || []).map((axis) => [
      axis.key,
      legacyVariant.options[axis.optionPosition - 1],
    ]));
  }
  #v2CommerceVariant(legacyVariant, count = this.count) {
    if (this.v2Mode !== 'active' || !this.v2Model || !legacyVariant) return legacyVariant || null;
    const normalized = this.v2Model.resolveVariant(this.#v2Selections(count));
    if (!normalized || normalized.id !== String(legacyVariant.id)) return null;
    return {
      ...legacyVariant,
      id: normalized.id,
      available: normalized.available,
      price: normalized.price.shopifyAmount,
      money: normalized.price.formatted,
      options: [...this.v2Contract.axes]
        .sort((left, right) => left.optionPosition - right.optionPosition)
        .map((axis) => normalized.selections[axis.key]),
      allocations: normalized.allocations.map((allocation) => ({
        planId: allocation.planId,
        price: allocation.price.shopifyAmount,
        money: allocation.price.formatted,
        savings: allocation.savings.shopifyAmount,
        savingsMoney: allocation.savings.formatted,
      })),
      sub: normalized.subscriptionConfig,
    };
  }
  #variantForCount(count) {
    const legacyVariant = this.#legacyVariantForCount(count);
    if (this.v2Mode !== 'active') return legacyVariant;
    return this.#v2CommerceVariant(legacyVariant, count);
  }
  #allocationFor(variant, plan) {
    if (!variant || !plan || !Array.isArray(variant.allocations)) return null;
    return variant.allocations.find((allocation) =>
      String(allocation.planId) === String(plan.id) &&
      Number.isSafeInteger(allocation.price) &&
      allocation.price >= 0) || null;
  }
  cardPrice(variant, plan = this.currentPlan()) {
    return resolveShopifyPdpPrice(variant, this.subscribe ? this.#allocationFor(variant, plan) : null, this.subscribe);
  }
  cardMoney(variant, price) {
    if (!price) return '';
    return !this.showDerived() && price.money
      ? price.money
      : this.money(price.amount);
  }
  #countForVariant(v) {
    if (!v) return null;
    const nums = (v.options || []).flatMap((o) => this.#numsIn(o));
    for (const f of this.formats) for (const c of f.counts) {
      if (nums.includes(c.bags)) return { format: f, count: c };
    }
    return null;
  }
  #recordV2Observation() {
    if (this.v2Mode !== 'observe' || !this.v2Model) return;
    const legacyVariant = this.#legacyVariantForCount(this.count);
    const normalizedVariant = this.v2Model.resolveVariant(this.#v2Selections(this.count));
    const mismatches = [];
    if (String(legacyVariant?.id ?? '') !== String(normalizedVariant?.id ?? '')) mismatches.push('variant');
    if (legacyVariant && normalizedVariant) {
      if (Boolean(legacyVariant.available) !== normalizedVariant.available) mismatches.push('availability');
      if (Number(legacyVariant.price) !== normalizedVariant.price.shopifyAmount) mismatches.push('price');
      const legacyAllocations = (legacyVariant.allocations || [])
        .map((allocation) => {
          const savings = Number.isSafeInteger(allocation.savings)
            ? allocation.savings
            : Number(legacyVariant.price) - Number(allocation.price);
          return `${allocation.planId}:${allocation.price}:${savings}`;
        })
        .sort();
      const normalizedAllocations = normalizedVariant.allocations
        .map((allocation) => `${allocation.planId}:${allocation.price.shopifyAmount}:${allocation.savings.shopifyAmount}`)
        .sort();
      if (legacyAllocations.join('|') !== normalizedAllocations.join('|')) mismatches.push('allocations');

      const normalizedPlans = resolveConfiguredPlanSet(this.v2Model, normalizedVariant.id).plans
        .map((plan) => plan.id)
        .sort();
      const legacyPlans = [...new Set(this.count.freqs || [])]
        .map((frequency) => this.#legacyPlanForFrequency(frequency))
        .filter((plan) => plan && this.#allocationFor(legacyVariant, plan))
        .map((plan) => String(plan.id))
        .sort();
      if (normalizedPlans.join('|') !== legacyPlans.join('|')) mismatches.push('plans');

      const quantityInput = this.nativeForm?.querySelector('input[name="quantity"]');
      const quantityOwner = quantityInput?.closest('quantity-selector-component');
      if (
        quantityInput instanceof HTMLInputElement
        && quantityOwner instanceof HTMLElement
        && quantityOwner.dataset.variantId === normalizedVariant.id
      ) {
        const nativeRule = {
          min: Number(quantityInput.min || 1),
          max: quantityInput.max ? Number(quantityInput.max) : null,
          step: Number(quantityInput.step || 1),
          cartQuantity: Number(quantityInput.dataset.cartQuantity || 0),
        };
        if (JSON.stringify(normalizedVariant.quantityRule) !== JSON.stringify(nativeRule)) mismatches.push('quantity');
      }
    }
    this.dataset.pogiV2Observe = mismatches.length ? 'mismatch' : 'match';
    if (mismatches.length) {
      console.warn('[pogi-buy-box-v2] Compostable observation mismatch:', {
        mismatches,
        legacyVariantId: legacyVariant?.id ?? null,
        normalizedVariantId: normalizedVariant?.id ?? null,
      });
    }
  }
  priceMoneyFor(formatName, count, plan = this.currentPlan()) {
    const variant = this.#variantForCount(count);
    const price = this.cardPrice(variant, plan);
    return price ? this.cardMoney(variant, price) : '';
  }

  #lowestPurchaseCount(f, plan = this.currentPlan()) {
    return f.counts.reduce((best, count) => {
      const price = this.cardPrice(this.#variantForCount(count), plan);
      if (!price) return best;
      if (!best) return { count, price };
      return price.amount < best.price.amount ? { count, price } : best;
    }, null)?.count || f.counts[0];
  }

  // "From" means the lowest absolute purchase price in this format. It must
  // not reuse `cheapest()`, whose separate job is finding the best price per bag.
  fromMoney(f, plan = this.currentPlan()) {
    return this.priceMoneyFor(f.name, this.#lowestPurchaseCount(f, plan), plan);
  }
  months(bags) { return pdpUsageDuration(bags, this.getDailyUsage(), 'months'); }
  // Lowest price-per-bag count in a format (the SAVE anchor).
  cheapest(f, plan = this.currentPlan()) {
    return f.counts.reduce((best, count) => {
      const price = this.cardPrice(this.#variantForCount(count), plan);
      if (!price || !count.bags) return best;
      if (!best || (price.amount / count.bags) < (best.price.amount / best.count.bags)) {
        return { count, price };
      }
      return best;
    }, null);
  }

  /* ---------- native variant mapping ---------- */
  #detectOptionPositions() {
    // POGI FIX 2026-07-23 (L4-001): detect by DIGITS, not label equality — the
    // count axis is the option position whose values carry the known bags
    // numbers; the other position is the format axis. Locale-proof.
    this.formatPos = -1; this.countPos = -1;
    const variants = this.data.variants || [];
    if (!variants.length) return;
    const bagNums = new Set();
    this.formats.forEach((f) => f.counts.forEach((c) => bagNums.add(c.bags)));
    const nOpts = variants[0].options.length;
    for (let p = 0; p < nOpts; p++) {
      const hits = variants.filter((v) => this.#numsIn(v.options[p]).some((n) => bagNums.has(n))).length;
      if (hits > 0 && this.countPos < 0) this.countPos = p;
    }
    for (let p = 0; p < nOpts; p++) if (p !== this.countPos && this.formatPos < 0) this.formatPos = p;
  }
  #matchedVariant() {
    return this.#variantForCount(this.count); // numeric contract (L4-001) — labels are display-only
  }
  #setNativeOption(fieldsetIndex, value) {
    if (!this.nativePicker || fieldsetIndex < 0) return;
    const inputs = this.nativePicker.querySelectorAll(`input[data-fieldset-index="${fieldsetIndex}"]`);
    for (const input of inputs) {
      if (this.#wnorm(input.value) === this.#wnorm(value) && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }
  }
  #driveNative() {
    if (this.v2Mode === 'active' && this.v2Adapter && this.v2Model) {
      const variant = this.#matchedVariant();
      if (!variant) throw new Error('The selected Compostable options do not resolve to one variant');
      this.v2Adapter.selectVariant(variant.id);
      return;
    }
    // Drive with the MATCHED VARIANT's own option strings (same source the native
    // inputs render), never the theme-translated model labels (L4-001 drift).
    const v = this.#matchedVariant();
    if (v) {
      this.#setNativeOption(this.formatPos, v.options[this.formatPos]);
      this.#setNativeOption(this.countPos, v.options[this.countPos]);
    } else {
      this.#setNativeOption(this.formatPos, this.format.name);
      this.#setNativeOption(this.countPos, this.count.label);
    }
  }

  /* ---------- selling plan ---------- */
  plansList() { const sp = this.data.sellingPlan; return (sp && sp.plans) || []; }
  // The live plan matching the chosen interval (by month count). Never substitute
  // another cadence: an empty selling_plan would submit one-time, while the first
  // product plan could silently schedule a different delivery interval.
  #legacyPlanForFrequency(frequency) {
    const plans = this.plansList();
    if (!plans.length) return null;
    const wantMo = Math.round(frequency / 30);
    const match = plans.find((p) => {
      const pi = pogiParseFreq(p.name); // locale-proof (POGI L10N FIX 2026-07-23)
      if (!pi) return false;
      if (pi.u === 'month') return pi.n === wantMo;
      if (pi.u === 'week') return Math.round(pi.n / 4) === wantMo;
      return false;
    });
    return match || null;
  }
  #planForFrequency(frequency) {
    if (this.v2Mode !== 'active' || !this.v2Model) return this.#legacyPlanForFrequency(frequency);
    const variant = this.#matchedVariant();
    if (!variant) return null;
    const wantMo = Math.round(frequency / 30);
    const matches = resolveConfiguredPlanSet(this.v2Model, variant.id).plans.filter((plan) => {
      const parsed = pogiParseFreq(plan.name);
      if (!parsed) return false;
      if (parsed.u === 'month') return parsed.n === wantMo;
      if (parsed.u === 'week') return Math.round(parsed.n / 4) === wantMo;
      return false;
    });
    if (matches.length !== 1) return null;
    return this.plansList().find((plan) => String(plan.id) === matches[0].id) || null;
  }
  currentPlan() {
    return this.#planForFrequency(this.freq);
  }
  #availableFrequencies() {
    const variant = this.#matchedVariant();
    const configured = this.v2Mode === 'active' && this.v2Model && variant
      ? resolveConfiguredPlanSet(this.v2Model, variant.id).plans.map((plan) => {
        const parsed = pogiParseFreq(plan.name);
        return parsed?.u === 'month' ? parsed.n * 30 : null;
      }).filter(Boolean)
      : [...new Set(this.count.freqs || [30, 45, 60, 90])];
    return configured.filter((frequency) => {
      const plan = this.#planForFrequency(frequency);
      return Boolean(plan && this.#allocationFor(variant, plan));
    });
  }
  /* ---------- rendering ---------- */
  #renderFormats() {
    const wrap = this.querySelector('[data-pogi-formats]');
    if (!wrap) return;
    wrap.innerHTML = this.formats.map((f) => {
      const glyph = FORMAT_GLYPH[f.key] || '';
      return `
      <button type="button" class="pogi-bb__format" role="radio" data-pogi-format="${this.#esc(f.key)}" aria-checked="false">
        ${glyph ? `<span class="pogi-bb__format-glyph" aria-hidden="true">${glyph}</span>` : ''}
        <span class="pogi-bb__format-name">${this.#esc(f.name)}</span>
        <span class="pogi-bb__format-desc">${this.#esc(f.desc)}</span>
        <span class="pogi-bb__format-from">${this.t('js_from', 'from [[price]]', { price: this.fromMoney(f) })}</span>
      </button>`;
    }).join('');
  }

  #renderCounts() {
    const wrap = this.querySelector('[data-pogi-counts]');
    if (!wrap) return;
    const counts = this.format.counts;
    wrap.setAttribute('data-cols', String(counts.length === 3 ? 3 : (counts.length === 1 ? 1 : 2)));
    const plan = this.currentPlan();
    const cheap = this.cheapest(this.format, plan);
    wrap.innerHTML = counts.map((c) => {
      const variant = this.#variantForCount(c);
      const price = this.cardPrice(variant, plan);
      const role = c.tag
        ? { t: c.tag, cls: 'pogi-bb__count-role--accent' }
        : (() => { const r = ROLE_BY_BAGS[c.bags] || { t: c.subtitle }; return { t: r.k ? this.t('js_role_' + r.k, r.t) : r.t, cls: r.tone === 'accent' ? 'pogi-bb__count-role--accent' : (r.tone === 'olive' ? 'pogi-bb__count-role--olive' : '') }; })();
      const perLine = (!c.bags || !price) ? null : this.showDerived()
        ? this.t('js_per_unit_short', '[[cents]]¢/[[unit]]', { cents: (price.amount / c.bags).toFixed(1), unit: this.t('unit_bag', 'bag') })
        : this.t('js_per_unit_short_money', '[[money]]/[[unit]]', { money: this.unitMoney(price.amount, c.bags), unit: this.t('unit_bag', 'bag') });
      const save = this.showDerived() && cheap && c !== cheap.count && price
        ? calculateShopifyUnitSavings(cheap.price.amount, cheap.count.bags, price.amount, c.bags)
        : 0;
      return `
      <button type="button" class="pogi-bb__count" data-promoted="${Boolean(c.tag || [270,480].includes(c.bags))}" role="radio" data-pogi-count="${this.#esc(c.label)}" data-pogi-bags="${c.bags}" aria-checked="false">
        <span class="pogi-bb__count-top">
          <span class="pogi-bb__count-role ${role.cls}">${this.#esc(role.t)}</span>
          <span class="pogi-bb__count-check" aria-hidden="true">✓</span>
        </span>
        <span class="pogi-bb__count-mid">
          <span class="pogi-bb__count-label">${this.#esc(this.#countHead(c.label))}</span>
          <span class="pogi-bb__count-price">${this.cardMoney(variant, price)}</span>
        </span>
        <span class="pogi-bb__count-bot">
          <span class="pogi-bb__count-sub">${this.#esc(this.#countSub(c))}</span>
          <span class="pogi-bb__count-anchor">
            ${perLine ? `<span class="pogi-bb__count-per">${perLine}</span>` : ''}
            ${save > 0 ? `<span class="pogi-bb__count-save">${this.t('js_save_caps', 'SAVE [[amount]]', { amount: this.moneyWhole(save) })}</span>` : ''}
          </span>
        </span>
      </button>`;
    }).join('');
  }

  #renderFreqs() {
    const wrap = this.querySelector('[data-pogi-freq-opts]');
    if (!wrap) return;
    const freqs = this.#availableFrequencies();
    if (!freqs.includes(this.freq)) {
      this.freq = freqs.includes(this.count.defaultFreq)
        ? this.count.defaultFreq
        : (freqs[0] || null);
    }
    // Same approved locale strings as the regular-bags interval labels.
    const monthsLabel = (n) => this.t('js_freq', '[[dur]]', {
      dur: this.t('js_dur', '[[n]] [[unit]]', { n, unit: this.t(n > 1 ? 'js_unit_months' : 'js_unit_month', n > 1 ? 'months' : 'month') }),
    });
    renderPdpIntervalSelect(this, freqs.map((d) => ({
      value: d, days: d, months: true,
      label: monthsLabel(d / 30),
    })), this.freq, (value) => {
      this.pogiExplicitInterval = true;
      this.freq = value;
      this.#renderFormats();
      this.#renderCounts();
      this.#commitUpdate();
    });
  }

  /* ---------- events ---------- */
  #bind() {
    this.addEventListener('click', (e) => {
      const fmt = e.target.closest('[data-pogi-format]');
      const cnt = e.target.closest('[data-pogi-count]');
      const mode = e.target.closest('[data-pogi-mode]');
      const freq = e.target.closest('[data-pogi-freq]');
      const dt = e.target.closest('[data-pogi-desc-toggle]');
      const dec = e.target.closest('[data-pogi-qty-dec]');
      const inc = e.target.closest('[data-pogi-qty-inc]');
      if (fmt) { this.#setFormat(fmt.dataset.pogiFormat); }
      else if (cnt) { this.#setCount(cnt.dataset.pogiCount); }
      else if (mode) { this.subscribe = mode.dataset.pogiMode === 'subscribe'; this.#renderFormats(); this.#renderCounts(); this.#commitUpdate(); }
      else if (freq) { this.pogiExplicitInterval = true; this.freq = parseInt(freq.dataset.pogiFreq, 10); this.#renderFreqs(); this.#renderFormats(); this.#renderCounts(); this.#commitUpdate(); }
      else if (dt) { const d = this.querySelector('[data-pogi-desc]'); const o = d.classList.toggle('is-open'); dt.textContent = o ? this.t('read_less', 'Read less') : this.t('read_more', 'Read more'); dt.setAttribute('aria-expanded', String(o)); }
      else if (dec) { this.#changeQty(-1); }
      else if (inc) { this.#changeQty(1); }
    }, { signal: this.v2Events.signal });
    // A11Y (audit P3, 2026-07-23): ONE delegated keydown per component root —
    // roving-tabindex arrow-key support for the radio groups (see #roveKey).
    this.addEventListener('keydown', (e) => this.#roveKey(e), { signal: this.v2Events.signal });
  }

  #commitUpdate() {
    try {
      this.update();
    } catch (error) {
      if (this.v2Mode !== 'active') throw error;
      console.error('[pogi-buy-box-v2] Compostable update failed closed:', error);
      this.#failV2(error);
    }
  }

  #commitSelection() {
    try {
      this.#driveNative();
      this.update();
    } catch (error) {
      if (this.v2Mode !== 'active') throw error;
      console.error('[pogi-buy-box-v2] Compostable selection failed closed:', error);
      this.#failV2(error);
    }
  }

  /* ---- a11y: roving tabindex (audit P3 + countersign #19, 2026-07-23) ---- */
  // ONE tab stop per radio group: tabindex 0 on the checked ELIGIBLE radio
  // (first eligible if none), -1 on every other radio — hidden/disabled ones
  // included, so they can never hold a stale tab stop. Called at the END of
  // update(), AFTER the [hidden] toggles, so eligibility is judged on the
  // pass's final visibility state. Behavior-additive: click handling is
  // untouched.
  #rove() {
    this.querySelectorAll(POGI_ROVE_GROUPS).forEach((g) => {
      const all = [...g.querySelectorAll('[role="radio"]')];
      const radios = all.filter(POGI_ROVE_ELIGIBLE);
      all.forEach((b) => b.setAttribute('tabindex', '-1')); // hidden/disabled are never the tab stop
      if (!radios.length) return;
      const on = radios.find((b) => b.getAttribute('aria-checked') === 'true') || radios[0];
      on.setAttribute('tabindex', '0'); // checked-and-eligible, else first ELIGIBLE
    });
  }
  // Arrow keys move focus AND activate the next/prev radio with wrap-around;
  // Home/End jump to first/last. Activation goes through .click() — the exact
  // path pointer input takes — so selection, native-picker drive and update()
  // all reuse the existing delegated click handler above.
  #roveKey(e) {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) return;
    const radio = e.target.closest('[role="radio"]');
    const group = radio && radio.closest(POGI_ROVE_GROUPS);
    if (!group) return; // not one of our groups (e.g. the sizing modal) — leave the key alone
    const radios = [...group.querySelectorAll('[role="radio"]')].filter(POGI_ROVE_ELIGIBLE); // arrows skip hidden/disabled radios
    const i = radios.indexOf(radio);
    if (i < 0) return;
    e.preventDefault();
    const next = e.key === 'Home' ? radios[0]
      : e.key === 'End' ? radios[radios.length - 1]
        : (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? radios[(i + 1) % radios.length]
          : radios[(i - 1 + radios.length) % radios.length];
    if (next === radio) return;
    next.focus();
    next.click();
    // A click can rebuild the clicked group's innerHTML (the freq group here
    // re-renders on selection) — re-focus the fresh tab stop so keyboard context
    // is never dropped mid-interaction.
    if (!next.isConnected) { const nb = group.querySelector('[role="radio"][tabindex="0"]'); if (nb) nb.focus(); }
  }

  // Changing format keeps the current subscription cadence when the target
  // format supports that exact plan; otherwise it uses the format default.
  // The default pack is tried first so subscribe mode lands on the same pack
  // as one-time mode instead of the smallest pack that allocates the plan.
  #setFormat(key) {
    const f = this.formats.find((x) => x.key === key);
    if (!f) return;
    const priorFrequency = this.freq;
    const priorPlan = this.currentPlan();
    this.format = f;
    const defaultCount = f.counts[1] || f.counts[0];
    const cadenceMatch = this.subscribe && priorPlan
      ? [defaultCount, ...f.counts.filter((count) => count !== defaultCount)].filter(Boolean).find((count) => {
        const variant = this.#variantForCount(count);
        return variant?.available
          && this.#allocationFor(variant, priorPlan);
      })
      : null;
    this.count = cadenceMatch || defaultCount;
    this.freq = this.#availableFrequencies().includes(priorFrequency)
      ? priorFrequency
      : (this.count.defaultFreq || priorFrequency);
    this.#renderFreqs();
    this.#renderFormats();
    this.#renderCounts();
    this.#commitSelection();
  }
  // Preserve a chosen interval across counts when the exact allocation remains valid.
  #setCount(label) {
    const c = this.format.counts.find((x) => x.label === label);
    if (!c) return;
    this.count = c;
    const keepSizingCadence = (this.pogiExplicitInterval || this._preserveSizingCadence) && this.#availableFrequencies().includes(this.freq);
    if (c.defaultFreq && !keepSizingCadence) this.freq = c.defaultFreq;
    this.#renderFreqs();
    this.#renderFormats();
    this.#renderCounts();
    this.#commitSelection();
  }

  update() {
    const plan = this.currentPlan();
    const matched = this.#matchedVariant();
    if (this.v2Mode === 'active' && !matched) {
      throw new Error('The selected Compostable options do not resolve to one variant');
    }
    const allocation = this.#allocationFor(matched, plan);
    const subscriptionValid = Boolean(plan && allocation);
    const quantityState = this.v2Mode === 'active' && this.v2Model && matched
      ? this.v2Adapter.quantityState(matched.id, this.qty)
      : null;
    if (quantityState) {
      this.qty = quantityState.value;
      const quantityOutput = this.querySelector('[data-pogi-qty]');
      if (quantityOutput) quantityOutput.textContent = this.qty;
      const decrement = this.querySelector('[data-pogi-qty-dec]');
      const increment = this.querySelector('[data-pogi-qty-inc]');
      const activeStepper = [decrement, increment].find((button) => button && button === document.activeElement);
      if (decrement) decrement.disabled = this.qty <= quantityState.min;
      if (increment) {
        increment.disabled = !quantityState.canAdd
          || (quantityState.remaining !== null && this.qty + quantityState.step > quantityState.remaining);
      }
      // A focused stepper that disables itself would drop keyboard focus to the
      // page; move focus to the other stepper while that one is still usable.
      if (activeStepper?.disabled) [decrement, increment].find((button) => button && button !== activeStepper && !button.disabled)?.focus();
    }

    // Selected states
    this.querySelectorAll('[data-pogi-format]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.pogiFormat === this.format.key)));
    this.querySelectorAll('[data-pogi-count]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.pogiCount === this.count.label)));
    this.querySelectorAll('[data-pogi-mode]').forEach((b) => b.setAttribute('aria-checked', String((b.dataset.pogiMode === 'subscribe') === this.subscribe)));
    const subscribeMode = this.querySelector('[data-pogi-mode="subscribe"]');
    if (subscribeMode) {
      subscribeMode.disabled = !subscriptionValid;
      subscribeMode.setAttribute('aria-disabled', String(!subscriptionValid));
    }
    const intervalSelect = this.querySelector('[data-pogi-interval-select]');
    if (intervalSelect) intervalSelect.value = String(this.freq);
    this.querySelectorAll('[data-pogi-freq]').forEach((b) => b.setAttribute('aria-checked', String(parseInt(b.dataset.pogiFreq, 10) === this.freq)));

    const fl = this.querySelector('[data-pogi-format-label]');
    if (fl) fl.textContent = this.format.name;

    const base = matched && Number.isSafeInteger(matched.price) ? matched.price : 0;
    const unitPrice = this.subscribe && subscriptionValid ? allocation.price : base;
    const total = unitPrice * this.qty;

    const setT = (sel, val) => { const el = this.querySelector(sel); if (el) el.textContent = val; };
    // Sub card always shows the discounted price + struck base (independent of the
    // currently selected mode), but only when an exact live cadence exists.
    const savings = subscriptionValid ? Math.max(0, base - allocation.price) : 0;
    const otPrice = resolveShopifyPdpPrice(matched, null, false);
    const otMoney = this.cardMoney(matched, otPrice);
    setT('[data-pogi-sub-price]', subscriptionValid
      ? (allocation.money || this.money(allocation.price))
      : otMoney);
    setT('[data-pogi-sub-was]', subscriptionValid && savings > 0 ? otMoney : '');
    setT('[data-pogi-ot-price]', otMoney);
    const badge = this.querySelector('[data-pogi-save-badge]');
    if (badge) badge.textContent = (subscriptionValid && this.showDerived() && savings > 0)
      ? this.t('js_save', 'Save [[amount]]', { amount: this.money(savings) })
      : '';

    // Subscribe expander
    const subx = this.querySelector('[data-pogi-subx]');
    if (subx) subx.hidden = !this.subscribe || !subscriptionValid;
    const subxDesc = this.querySelector('[data-pogi-subx-desc]');
    if (subxDesc) subxDesc.textContent = this.t('js_choose_interval', 'Choose a delivery interval that fits your use.');

    // Native selling plan + ATC. Active mode synchronizes through the one shared
    // Horizon bridge; legacy/observe retain the existing controller behavior.
    if (this.v2Mode === 'active' && this.v2Adapter && matched) {
      const state = this.v2Adapter.syncPurchaseInputs({
        variantId: matched.id,
        sellingPlanId: this.subscribe && subscriptionValid ? plan.id : null,
        quantity: this.qty,
      });
      this.qty = state.value;
    } else {
      this.#ensureSellingPlanInput();
      const planInput = this.nativeForm && this.nativeForm.querySelector('input[name="selling_plan"]');
      if (planInput) planInput.value = (this.subscribe && subscriptionValid) ? plan.id : '';
      const idInput = this.nativeForm && this.nativeForm.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = matched ? matched.id : '';
        idInput.dataset.pogiSync = '1';
      }
    }

    renderPdpUsage(this, {
      recommendationAllowed: quantityState?.canAdd !== false,
      enabled: Boolean(matched?.available),
      unitsPerPack: this.count.bags, quantity: this.qty, perDay: this.getDailyUsage(),
      subscribed: this.subscribe && subscriptionValid, locale: this.v2Contract?.market?.displayLocale,
      maxUnit: 'months',
    });

    const atc = this.querySelector('[data-pogi-atc]');
    const atcLabel = this.querySelector('[data-pogi-atc-label]');
    let identityPrice = '';
    const available = Boolean(matched && matched.available);
    const invalidSubscription = this.subscribe && !subscriptionValid;
    const invalidQuantity = quantityState ? available && !quantityState.canAdd : false;
    if (atc) {
      atc.disabled = !available || invalidSubscription || invalidQuantity;
      const atcMoney = this.money(total);
      identityPrice = atcMoney;
      atcLabel.textContent = invalidSubscription || invalidQuantity || !matched
        ? this.t('js_sold_out', 'Unavailable')
        : available
          ? this.t('js_atc_add', 'Add to cart · ') + atcMoney
          : this.t('js_sold_out', 'Sold out');
    }

    const formatHelp = this.querySelector('[data-pogi-format-help]');
    if (formatHelp) formatHelp.textContent = this.format?.desc || '';
    syncPdpIdentity(this, { price: identityPrice, available: Boolean(atc && !atc.disabled),
      subscribed: this.subscribe && subscriptionValid, quantity: this.qty });

    // Free-shipping nudge — subscriptions always ship free; one-time counts toward the threshold.
    // (When not subscribed, `total` === base × qty, i.e. the full one-time total.)
    const ship = this.querySelector('[data-pogi-ship]');
    if (ship) {
      const msg = ship.querySelector('[data-pogi-ship-msg]');
      const fill = ship.querySelector('[data-pogi-ship-fill]');
      if (this.I?.js_shipping_supported === false) {
        ship.classList.remove('is-unlocked');
        if (msg) msg.innerHTML = this.t('js_ship_always_html', '<strong>See delivery options at checkout.</strong>');
        if (fill) fill.style.width = '0%';
      } else if (this.subscribe && subscriptionValid) {
        ship.classList.add('is-unlocked');
        if (msg) msg.innerHTML = this.t('js_ship_always_html', '<strong>Free shipping, always.</strong>');
        if (fill) fill.style.width = '100%';
      } else {
        const remaining = Math.max(0, this.shipThreshold - total);
        if (remaining === 0) {
          ship.classList.add('is-unlocked');
          if (msg) msg.innerHTML = this.t('js_ship_unlocked_html', '<strong>Free shipping unlocked.</strong>');
        } else {
          ship.classList.add('is-unlocked');
          if (msg) msg.innerHTML = this.t('js_ship_remaining_html', "You're <strong>[[amount]]</strong> from free shipping.", { amount: this.money(remaining) });
        }
        if (fill) fill.style.width = '100%';
      }
    }
    // Roving tabindex LAST — after the [hidden] panel toggles above, so a
    // hidden/disabled radio is never left holding a group's tab stop
    // (Codex countersign #19, 2026-07-23).
    this.#rove();
    this.#recordV2Observation();
  }

  #ensureSellingPlanInput() {
    if (!this.nativeForm) return;
    if (!this.nativeForm.querySelector('input[name="selling_plan"]')) {
      const i = document.createElement('input'); i.type = 'hidden'; i.name = 'selling_plan'; this.nativeForm.appendChild(i);
    }
  }
  isSizingFormatAvailable(key) {
    const format = this.formats.find((value) => value.key === key);
    const plan = this.currentPlan();
    if (!format) return false;
    const cadenceMatch = this.subscribe && plan ? format.counts.find((count) => {
      const candidate = this.#variantForCount(count);
      return (count.freqs || []).includes(this.freq) && candidate?.available
        && this.#allocationFor(candidate, plan);
    }) : null;
    const count = cadenceMatch || format.counts[1] || format.counts[0];
    const candidate = count && this.#variantForCount(count);
    const quantity = candidate && this.v2Adapter?.quantityState(candidate.id, this.qty);
    return Boolean(candidate?.available
      && (!this.subscribe || ((count.freqs || []).includes(this.freq) && this.#allocationFor(candidate, plan)))
      && (!quantity || (quantity.value === this.qty && quantity.canAdd)));
  }
  isSizingCountAvailable(value) {
    const count = this.format.counts.find((option) => option.label === value);
    const candidate = count ? this.#variantForCount(count) : null;
    const quantity = candidate && this.v2Adapter?.quantityState(candidate.id, this.qty);
    return Boolean(
      candidate?.available
      && (!quantity || (quantity.value === this.qty && quantity.canAdd))
      && (!this.subscribe || ((count.freqs || []).includes(this.freq) && this.#allocationFor(candidate, this.currentPlan())))
    );
  }
  // Public bridge used only by the PDP bag calculator. Selection travels through
  // the same option button as a shopper click; quantity is preserved;
  // Format, purchase mode and the shopper's exact cadence remain untouched. The
  // calculator only selects a pack. The shopper reviews it before using ATC.
  applySizingRecommendation(value) {
    const option = [...this.querySelectorAll('[data-pogi-count]')]
      .find((button) => button.dataset.pogiCount === value && button.style.display !== 'none');
    if (!option || !this.isSizingCountAvailable(value)) return false;
    const intent = {
      quantity: this.qty,
      subscribe: Boolean(this.subscribe),
      frequency: this.freq,
      planId: this.currentPlan()?.id ?? null,
      nativePlanId: this.nativeForm?.querySelector('input[name="selling_plan"]')?.value || '',
      format: this.format.key,
    };
    this._preserveSizingCadence = intent.subscribe;
    try {
      option.click();
    } finally {
      this._preserveSizingCadence = false;
    }
    const intentPreserved = (
      this.qty === intent.quantity
      && Boolean(this.subscribe) === intent.subscribe
      && this.format.key === intent.format
      && (!intent.subscribe || (
        this.freq === intent.frequency
        && String(this.currentPlan()?.id ?? '') === String(intent.planId ?? '')
        && (this.nativeForm?.querySelector('input[name="selling_plan"]')?.value || '') === intent.nativePlanId
      ))
    );
    return intentPreserved;
  }
  #changeQty(direction) {
    const variant = this.#matchedVariant();
    if (this.v2Mode === 'active' && this.v2Model && !variant) {
      this.#failV2(new Error('Cannot change quantity without one resolved Compostable variant'));
      return;
    }
    if (this.v2Mode === 'active' && this.v2Model) {
      this.qty = this.v2Adapter.stepQuantity(variant.id, this.qty, direction).value;
    } else {
      this.qty = Math.max(1, this.qty + (direction < 0 ? -1 : 1));
    }
    this.#syncQty();
  }
  #syncQty() {
    const out = this.querySelector('[data-pogi-qty]');
    if (out) out.textContent = this.qty;
    if (this.v2Mode !== 'active') {
      const nq = this.nativeForm && this.nativeForm.querySelector('input[name="quantity"]');
      if (nq) { nq.value = this.qty; nq.dispatchEvent(new Event('change', { bubbles: true })); }
    }
    this.#commitUpdate();
  }

  #injectGalleryBadge() {
    const text = (this.dataset.galleryBadge || '').trim();
    if (!text) return;
    const tile = this.section.querySelector('.product-media-container');
    if (!tile || tile.querySelector('.pogi-gallery-badge')) return;
    const chip = document.createElement('span');
    chip.className = 'pogi-gallery-badge';
    chip.textContent = text;
    tile.appendChild(chip);
  }
}

if (!customElements.get('pogi-buy-box-compostable')) {
  customElements.define('pogi-buy-box-compostable', PogiBuyBoxCompostable);
}
